import { spawn } from 'node:child_process';

export interface CliAuthStartResponse {
  session_id: string;
  auth_url: string;
  ws_url: string;
  expires_at: number;
}

interface CliAuthWsAuthenticatedMessage {
  type: 'authenticated';
  session_id: string;
  bootstrap_code?: string;
  exchange_code?: string;
  command?: string;
  expires_at?: number;
}

interface CliAuthWsTerminalMessage {
  type: 'cancelled' | 'expired';
  reason_code?: string;
}

type CliAuthWsMessage =
  | CliAuthWsAuthenticatedMessage
  | CliAuthWsTerminalMessage
  | { type: string; [key: string]: unknown };

export interface CliAuthExchangeResponse {
  access_token: string;
  access_token_expires_at: number;
  refresh_token: string;
  refresh_token_expires_at: number;
  org: { id: string; slug: string; name: string };
  user: { id: string; email: string; role: string };
}

export interface CliAuthRefreshResponse {
  access_token: string;
  access_token_expires_at: number;
  refresh_token: string;
  refresh_token_expires_at: number;
  org: { id: string; slug: string; name: string };
  user: { id: string; email: string; role: string };
}

interface RuntimeWebSocket {
  send?: (data: string) => void;
  close(): void;
  onopen: (() => void) | null;
  onerror: ((event: unknown) => void) | null;
  onclose: ((event: unknown) => void) | null;
  onmessage: ((event: { data: unknown }) => void) | null;
}

function getWebSocketCtor(): ((url: string) => RuntimeWebSocket) {
  const maybeCtor = (globalThis as unknown as { WebSocket?: new (url: string) => RuntimeWebSocket }).WebSocket;
  if (!maybeCtor) {
    throw new Error('WebSocket is not available in this runtime');
  }
  return (url: string) => new maybeCtor(url);
}

function sendWsPoll(ws: RuntimeWebSocket): void {
  if (typeof ws.send !== 'function') return;
  try {
    ws.send(JSON.stringify({ type: 'poll' }));
  } catch {
    // no-op
  }
}

export function resolvePlatformApiEndpoint(raw?: string): string {
  const fromPlatformEndpoint = process.env.PLATFORM_ENDPOINT;
  const fromLegacyEnv = process.env.STANDARDAGENTS_PLATFORM_API_URL;
  const endpoint = (raw || fromPlatformEndpoint || fromLegacyEnv || 'https://api.standardagents.ai').trim();
  if (!endpoint) return 'https://api.standardagents.ai';
  return endpoint.replace(/\/+$/, '');
}

export function resolvePlatformAuthBrowserUrl(rawApiEndpoint?: string): string {
  const explicit = (
    process.env.CLI_AUTH_BROWSER_URL
    || process.env.STANDARDAGENTS_AUTH_URL
    || process.env.PLATFORM_AUTH_PUBLIC_URL
  )?.trim();
  if (explicit) {
    return explicit.replace(/\/+$/, '');
  }

  const apiEndpoint = resolvePlatformApiEndpoint(rawApiEndpoint);
  try {
    const url = new URL(apiEndpoint);
    if (url.hostname.startsWith('platform-api.')) {
      url.hostname = `platform-auth.${url.hostname.slice('platform-api.'.length)}`;
      return url.origin;
    }
    if (url.hostname.startsWith('api.')) {
      url.hostname = `auth.${url.hostname.slice('api.'.length)}`;
      return url.origin;
    }
    return url.origin;
  } catch {
    return 'https://auth.standardagents.ai';
  }
}

async function parseError(response: Response): Promise<string> {
  const fallback = `Request failed (${response.status})`;
  try {
    const body = await response.json() as {
      error?: { code?: string; message?: string };
    };
    if (body.error?.code && body.error?.message) {
      return `${body.error.code}: ${body.error.message}`;
    }
    if (body.error?.message) {
      return body.error.message;
    }
    return fallback;
  } catch {
    return fallback;
  }
}

export async function startCliAuthSession(
  endpoint: string,
  options: { purpose?: 'auth' } = {},
): Promise<CliAuthStartResponse> {
  const response = await fetch(`${endpoint}/cli/auth/start`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    // purpose 'auth' routes the browser to the platform's dedicated /cli-auth
    // page (instant, no-wizard handshake) instead of project onboarding.
    body: options.purpose ? JSON.stringify({ purpose: options.purpose }) : undefined,
  });
  if (!response.ok) {
    throw new Error(await parseError(response));
  }
  return response.json() as Promise<CliAuthStartResponse>;
}

export async function cancelCliAuthSession(
  endpoint: string,
  sessionId: string,
): Promise<void> {
  await fetch(`${endpoint}/cli/auth/cancel`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ session_id: sessionId }),
  }).catch(() => undefined);
}

export async function exchangeCliAuthSession(
  endpoint: string,
  sessionId: string,
  exchangeCode: string,
): Promise<CliAuthExchangeResponse> {
  const response = await fetch(`${endpoint}/cli/auth/exchange`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ session_id: sessionId, exchange_code: exchangeCode }),
  });
  if (!response.ok) {
    throw new Error(await parseError(response));
  }
  return response.json() as Promise<CliAuthExchangeResponse>;
}

export async function refreshCliAuthToken(
  endpoint: string,
  refreshToken: string,
): Promise<CliAuthRefreshResponse> {
  const response = await fetch(`${endpoint}/cli/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: refreshToken }),
  });
  if (!response.ok) {
    throw new Error(await parseError(response));
  }
  return response.json() as Promise<CliAuthRefreshResponse>;
}

export async function revokeCliAuthToken(
  endpoint: string,
  refreshToken: string,
): Promise<void> {
  const response = await fetch(`${endpoint}/cli/auth/revoke`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: refreshToken }),
  });
  if (!response.ok) {
    throw new Error(await parseError(response));
  }
}

export async function waitForCliAuthCompletion(
  wsUrl: string,
  expiresAt: number,
): Promise<{ bootstrapCode: string; command?: string }> {
  const createWebSocket = getWebSocketCtor();
  const ws = createWebSocket(wsUrl);

  return new Promise((resolve, reject) => {
    let settled = false;
    const timeoutMs = Math.max(1_000, expiresAt - Date.now() + 5_000);
    const closeSocket = () => {
      try {
        ws.close();
      } catch {
        // no-op
      }
    };
    // Bounded compatibility fallback, NOT the delivery mechanism: the
    // platform pushes completion to a subscribed socket (PL-01), and the
    // on-open poll below requests one fresh snapshot to close the
    // subscribe/complete race. This slow cadence only rescues delivery on
    // pre-push platform deploys, where the browser completion can land on a
    // different isolate and only a poll re-reads it. It exits with the
    // session (settle/expiry). The former cadence was 750ms (~80
    // messages/minute per waiting CLI, each a potential D1 read).
    const compatibilityPoll = setInterval(() => {
      if (settled) return;
      sendWsPoll(ws);
    }, 5_000);
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      clearInterval(compatibilityPoll);
      closeSocket();
      reject(new Error('Authentication timed out. Run "agents login" to restart.'));
    }, timeoutMs);

    const settle = (fn: () => void) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      clearInterval(compatibilityPoll);
      closeSocket();
      fn();
    };

    ws.onopen = () => {
      // Prompt server-side delivery from websocket context.
      sendWsPoll(ws);
    };

    ws.onerror = () => {
      settle(() => reject(new Error('Failed to connect to auth session websocket.')));
    };

    ws.onclose = () => {
      settle(() => reject(new Error('Authentication session closed before completion.')));
    };

    ws.onmessage = (event: { data: unknown }) => {
      let message: CliAuthWsMessage;
      try {
        const raw = typeof event.data === 'string' ? event.data : String(event.data);
        message = JSON.parse(raw) as CliAuthWsMessage;
      } catch {
        return;
      }

      if (message.type === 'authenticated') {
        const bootstrapCode =
          typeof message.bootstrap_code === 'string'
            ? message.bootstrap_code
            : typeof message.exchange_code === 'string'
              ? message.exchange_code
              : '';

        if (!bootstrapCode) {
          settle(() => reject(new Error('Authentication completed without bootstrap code.')));
          return;
        }
        const command = typeof message.command === 'string' ? message.command : undefined;
        settle(() => resolve({ bootstrapCode, command }));
        return;
      }

      if (message.type === 'cancelled') {
        settle(() => reject(new Error('Authentication was cancelled. Run "agents login" to restart.')));
        return;
      }

      if (message.type === 'expired') {
        settle(() => reject(new Error('Authentication session expired. Run "agents login" to restart.')));
      }
    };
  });
}

export function openBrowser(url: string): boolean {
  try {
    if (process.platform === 'darwin') {
      const child = spawn('open', [url], { detached: true, stdio: 'ignore' });
      child.unref();
      return true;
    }
    if (process.platform === 'win32') {
      const child = spawn('cmd', ['/c', 'start', '', url], { detached: true, stdio: 'ignore' });
      child.unref();
      return true;
    }
    const child = spawn('xdg-open', [url], { detached: true, stdio: 'ignore' });
    child.unref();
    return true;
  } catch {
    return false;
  }
}
